import { UserPreferencesState } from './state';
import {
  UserPreferencesActionsUnion,
  UserPreferencesActionTypes,
  SaveUserPreferenceSuccess,
} from './actions';
import { AllotmentPreferences } from '../../types/allotment-preferences.types';
import { UserPreference, FilterSet } from '../../types/user-preferences.types';

const ALLOTMENT_PREFERENCES_NAME = 'allotmentPreferences';
const ALLOTMENT_PREFERENCES_PAYLOAD_KEY = 'allotmentPreferences';
const USER_PREFERENCES_PAYLOAD_KEY = 'userPreferences';

const upsertUserPreference = (
  userPreferences: UserPreference[],
  userPreference: UserPreference
): UserPreference[] => {
  const index = userPreferences.findIndex(
    (pref) =>
      pref.id === userPreference.id ||
      (pref.name === userPreference.name && pref.name === ALLOTMENT_PREFERENCES_NAME)
  );

  if (index === -1) {
    return [...userPreferences, userPreference];
  }

  const updatedUserPreferences = [...userPreferences];
  updatedUserPreferences[index] = userPreference;
  return updatedUserPreferences;
};

const getAllotmentPreferenceEntry = (
  userPreferences: UserPreference[] = []
): UserPreference | undefined =>
  userPreferences.find(
    (pref) =>
      pref.name === ALLOTMENT_PREFERENCES_NAME ||
      pref.userPreference?.[USER_PREFERENCES_PAYLOAD_KEY]?.[
      ALLOTMENT_PREFERENCES_PAYLOAD_KEY
      ]
  );

export const userPreferencesDefaultState: UserPreferencesState = {
  userPreferences: [],
  loaded: false,
  loading: false,
  saving: false,
  error: undefined,
};

export function userPreferencesReducer(
  state = userPreferencesDefaultState,
  action: UserPreferencesActionsUnion
): UserPreferencesState {
  switch (action.type) {
    case UserPreferencesActionTypes.Load:
      return {
        ...state,
        loading: true,
        loaded: false,
      };
    case UserPreferencesActionTypes.LoadSuccess:
      return {
        ...state,
        userPreferences: action.payload,
        loaded: true,
        loading: false,
        error: undefined,
      };
    case UserPreferencesActionTypes.LoadFail:
      return {
        ...state,
        userPreferences: [],
        loaded: false,
        loading: false,
        error: action.payload,
      };
    case UserPreferencesActionTypes.Save:
      return {
        ...state,
        saving: true,
      };
    case UserPreferencesActionTypes.SaveSuccess:
      // eslint-disable-next-line no-case-declarations
      const saveSuccessAction = action as SaveUserPreferenceSuccess;
      return {
        ...state,
        userPreferences: upsertUserPreference(
          state.userPreferences,
          saveSuccessAction.payload
        ),
        saving: false,
      };
    case UserPreferencesActionTypes.SaveFail:
      return {
        ...state,
        saving: false,
      };
    case UserPreferencesActionTypes.SetAllotmentPreferences:
      // eslint-disable-next-line no-case-declarations
      const updatedPreferences = action.payload
        ? upsertUserPreference(state.userPreferences, {
          id: ALLOTMENT_PREFERENCES_NAME,
          name: ALLOTMENT_PREFERENCES_NAME,
          userPreference: {
            [USER_PREFERENCES_PAYLOAD_KEY]: {
              [ALLOTMENT_PREFERENCES_PAYLOAD_KEY]: action.payload,
            },
          },
        })
        : state.userPreferences.filter(
          (pref) => pref.name !== ALLOTMENT_PREFERENCES_NAME
        );

      return {
        ...state,
        userPreferences: updatedPreferences,
      };
    case UserPreferencesActionTypes.Delete:
      return {
        ...state,
        saving: true,
      };
    case UserPreferencesActionTypes.DeleteSuccess:
      return {
        ...state,
        userPreferences: action.payload,
        saving: false,
      };
    case UserPreferencesActionTypes.DeleteFail:
      return {
        ...state,
        saving: false,
        error: action.payload,
      };
    default:
      return state;
  }
}

export const getSavedCalendarFilters = (
  userPrefState: UserPreferencesState
): FilterSet[] => {
  return userPrefState.userPreferences.map((pref: UserPreference) => ({
    id: pref.id,
    name: pref.name,
    filterSet: pref.userPreference['calendarFilters'],
  }));
};

export const getAllotmentPreferences = (
  userPrefState: UserPreferencesState
): AllotmentPreferences | null => {
  const allotmentPreferenceEntry = getAllotmentPreferenceEntry(
    userPrefState.userPreferences
  );

  return (
    allotmentPreferenceEntry?.userPreference?.[USER_PREFERENCES_PAYLOAD_KEY]?.[
    ALLOTMENT_PREFERENCES_PAYLOAD_KEY
    ] ||
    null
  );
};